// ** react imports
import { useState, useMemo, useEffect, useCallback } from 'react';

// ** api methods for user management
import {
  useRegisterUserByAdminMutation,
  useSetNewPasswordMutation,
  useRegisterUserMutation,
  useVerifyOtpMutation,
  useResendOtpMutation,
  useLoginUserMutation,
  useGetAllUsersQuery,
  useGetUserByIdQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useVerifyEmailUpdateMutation,
  useGetCustomerDetailByIdQuery,
  useLogoutAllUsersMutation,
} from '../../../redux/slices/users/slice';

export const useRegisterUserByAdmin = () => {
  try {
    // api call mutation
    const [
      registerUserByAdmin,
      {
        isLoading: isRegisterUserByAdminLoading,
        isError: isRegisterUserByAdminError,
        error: registerByAdminError,
      },
    ] = useRegisterUserByAdminMutation();

    // handlers
    const handleRegisterUserByAdmin = async data => {
      return await registerUserByAdmin(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isRegisterUserByAdminLoading,
        isRegisterUserByAdminError,
        registerByAdminError,
        registerUserByAdmin: handleRegisterUserByAdmin,
      }),
      [
        isRegisterUserByAdminLoading,
        isRegisterUserByAdminError,
        registerByAdminError,
        handleRegisterUserByAdmin,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error registering user by admin:', error);
    throw error;
  }
};

export const useSetNewPassword = () => {
  try {
    // api calls mutation
    const [
      setNewPassword,
      {
        isLoading: isSetNewPasswordLoading,
        isError: isSetNewPasswordError,
        error: setNewPasswordError,
      },
    ] = useSetNewPasswordMutation();

    // handlers
    const handleSetNewPassword = async data => {
      return await setNewPassword(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isSetNewPasswordLoading,
        isSetNewPasswordError,
        setNewPasswordError,
        setNewPassword: handleSetNewPassword,
      }),
      [
        isSetNewPasswordLoading,
        isSetNewPasswordError,
        setNewPasswordError,
        handleSetNewPassword,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error setting new password:', error);
    throw error;
  }
};

export const useRegisterUser = () => {
  try {
    // api calls mutation
    const [
      registerUser,
      {
        isLoading: isRegisterUserLoading,
        isError: isRegisterUserError,
        error: registerUserError,
      },
    ] = useRegisterUserMutation();

    // handlers
    const handleRegisterUser = async data => {
      return await registerUser(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isRegisterUserLoading,
        isRegisterUserError,
        registerUserError,
        registerUser: handleRegisterUser,
      }),
      [
        isRegisterUserLoading,
        isRegisterUserError,
        registerUserError,
        handleRegisterUser,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error registering user:', error);
    throw error;
  }
};

export const useVerifyOtp = () => {
  try {
    // api call mutations
    const [
      verifyOtp,
      {
        isLoading: isVerifyOtpLoading,
        isError: isVerifyOtpError,
        error: verifyOtpError,
      },
    ] = useVerifyOtpMutation();

    // handlers
    const handleVerifyOtp = async data => {
      return await verifyOtp(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isVerifyOtpLoading,
        isVerifyOtpError,
        verifyOtpError,
        verifyOtp: handleVerifyOtp,
      }),
      [isVerifyOtpLoading, isVerifyOtpError, verifyOtpError, handleVerifyOtp]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    throw error;
  }
};

export const useResendOtp = () => {
  try {
    // api call mutation
    const [
      resendOtp,
      {
        isLoading: isResendOtpLoading,
        isError: isResendOtpError,
        error: resendOtpError,
      },
    ] = useResendOtpMutation();

    // handlers
    const handleResendOtp = async data => {
      return await resendOtp(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isResendOtpLoading,
        isResendOtpError,
        resendOtpError,
        resendOtp: handleResendOtp,
      }),
      [isResendOtpLoading, isResendOtpError, resendOtpError, handleResendOtp]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error resending OTP:', error);
    throw error;
  }
};

export const useLoginUser = () => {
  try {
    // api call mutation
    const [
      loginUser,
      {
        isLoading: isLoginUserLoading,
        isError: isLoadingUserError,
        error: loginUserError,
      },
    ] = useLoginUserMutation();

    // handlers
    const handleLoginUser = async data => {
      return await loginUser(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isLoginUserLoading,
        isLoadingUserError,
        loginUserError,
        loginUser: handleLoginUser,
      }),
      [isLoginUserLoading, isLoadingUserError, loginUserError, handleLoginUser]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error while login user:', error);
    throw error;
  }
};

export const useGetAllUsers = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [query, setQuery] = useState({});

    // api call query
    const {
      data: getAllUsers,
      isLoading: isGetAllUsersLoading,
      isError: isGetAllUsersError,
      error: getAllUsersError,
      isFetching: isGetAllUsersFetching,
      refetch: refetchGetAllUsers,
    } = useGetAllUsersQuery(query, {
      skip: skipApi,
    });

    // handlers
    const handleGetAllUsers = useCallback(async query => {
      setSkipApi(false);
      setQuery(query);
    }, []);

    // effect
    useEffect(() => {
      if (getAllUsers !== undefined) {
        // pagination implementation was pending.
        setData({
          data: getAllUsers.data.data,
          total: getAllUsers.data.total,
        });
        setSkipApi(true);
      }
    }, [getAllUsers]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetAllUsersLoading,
        isGetAllUsersError,
        getAllUsersError,
        isGetAllUsersFetching,
        refetchGetAllUsers,
        getAllUsers: handleGetAllUsers,
      }),
      [
        data,
        isGetAllUsersLoading,
        isGetAllUsersError,
        getAllUsersError,
        isGetAllUsersFetching,
        refetchGetAllUsers,
        handleGetAllUsers,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw error;
  }
};

export const useGetUserById = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [id, setId] = useState('');

    // api call query
    const {
      data: getUserDetailsById,
      isLoading: isGetUserDetailsByIdLoading,
      isError: isGetUserDetailsByIdError,
      error: getUserDetailsByIdError,
      refetch: refetchGetUserDetailsByIdById,
    } = useGetUserByIdQuery(id, { skip: skipApi });

    // handlers
    const handleGetUserDetailsById = async id => {
      setSkipApi(false);
      setId(id);
    };

    // effect
    useEffect(() => {
      if (getUserDetailsById !== undefined) {
        setData(getUserDetailsById.data);
      }
    }, [getUserDetailsById]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetUserDetailsByIdLoading,
        isGetUserDetailsByIdError,
        getUserDetailsByIdError,
        refetchGetUserDetailsByIdById,
        getUserDetailsById: handleGetUserDetailsById,
      }),
      [
        data,
        isGetUserDetailsByIdLoading,
        isGetUserDetailsByIdError,
        getUserDetailsByIdError,
        refetchGetUserDetailsByIdById,
        handleGetUserDetailsById,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error getting customer details:', error);
    throw error;
  }
};

export const useUpdateUser = () => {
  try {
    // api call mutation
    const [
      updateUser,
      {
        isLoading: isUpdateUserLoading,
        isError: isUpdateUserError,
        error: updateUserError,
      },
    ] = useUpdateUserMutation();

    // handlers
    const handleUpdateUser = async (id, data) => {
      return await updateUser({ id, data });
    };

    //  memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isUpdateUserLoading,
        isUpdateUserError,
        updateUserError,
        updateUser: handleUpdateUser,
      }),
      [
        isUpdateUserLoading,
        isUpdateUserError,
        updateUserError,
        handleUpdateUser,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const useDeleteUser = () => {
  try {
    // api call mutation
    const [
      deleteUser,
      {
        isLoading: isDeleteUserLoading,
        isError: isDeleteUserError,
        error: deleteUserError,
      },
    ] = useDeleteUserMutation();

    // handlers
    const handleDeleteUser = async id => {
      return await deleteUser(id);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isDeleteUserLoading,
        isDeleteUserError,
        deleteUserError,
        deleteUser: handleDeleteUser,
      }),
      [
        isDeleteUserLoading,
        isDeleteUserError,
        deleteUserError,
        handleDeleteUser,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

export const useForgotPassword = () => {
  try {
    // api call mutation
    const [
      forgotPassword,
      {
        isLoading: isForgotPasswordLoading,
        isError: isForgotPasswordError,
        error: forgotPasswordError,
      },
    ] = useForgotPasswordMutation();

    // handlers
    const handleForgotPassword = async data => {
      return await forgotPassword(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isForgotPasswordLoading,
        isForgotPasswordError,
        forgotPasswordError,
        forgotPassword: handleForgotPassword,
      }),
      [
        isForgotPasswordLoading,
        isForgotPasswordError,
        forgotPasswordError,
        handleForgotPassword,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error in forgot password:', error);
    throw error;
  }
};

export const useResetPassword = () => {
  try {
    // api call mutation
    const [
      resetPassword,
      {
        isLoading: isResetPasswordLoading,
        isError: isResetPasswordError,
        error: resetPasswordError,
      },
    ] = useResetPasswordMutation();

    // handlers
    const handleResetPassword = async data => {
      return await resetPassword(data);
    };

    //  memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isResetPasswordLoading,
        isResetPasswordError,
        resetPasswordError,
        resetPassword: handleResetPassword,
      }),
      [
        isResetPasswordLoading,
        isResetPasswordError,
        resetPasswordError,
        handleResetPassword,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
};

export const useGetCustomerDetailsById = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [id, setId] = useState('');

    // api call query
    const {
      data: getCustomerDetailsById,
      isLoading: isGetCustomerDetailsByIdLoading,
      isError: isGetCustomerDetailsByIdError,
      error: getCustomerDetailsByIdError,
      refetch: refetchGetCustomerDetailsById,
    } = useGetCustomerDetailByIdQuery(id, { skip: skipApi });

    // handlers
    const handleGetCustomerDetailsById = async id => {
      console.log(id);
      setSkipApi(false);
      setId(id);
    };

    // effect
    useEffect(() => {
      if (getCustomerDetailsById !== undefined) {
        setData(getCustomerDetailsById.data);
      }
    }, [getCustomerDetailsById]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetCustomerDetailsByIdLoading,
        isGetCustomerDetailsByIdError,
        getCustomerDetailsByIdError,
        refetchGetCustomerDetailsById,
        getCustomerDetailsById: handleGetCustomerDetailsById,
      }),
      [
        data,
        isGetCustomerDetailsByIdLoading,
        isGetCustomerDetailsByIdError,
        getCustomerDetailsByIdError,
        refetchGetCustomerDetailsById,
        handleGetCustomerDetailsById,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error getting customer details:', error);
    throw error;
  }
};

export const useVerifyEmailUpdate = () => {
  try {
    // api call mutation
    const [
      verifyEmailUpdate,
      {
        isLoading: isVerifyEmailUpdateLoading,
        isError: isVerifyEmailUpdateError,
        error: verifyEmailUpdateError,
      },
    ] = useVerifyEmailUpdateMutation();

    // handlers
    const handleVerifyEmailUpdate = async ({ id, data }) => {
      return await verifyEmailUpdate({ id, data });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isVerifyEmailUpdateLoading,
        isVerifyEmailUpdateError,
        verifyEmailUpdateError,
        verifyEmailUpdate: handleVerifyEmailUpdate,
      }),
      [
        isVerifyEmailUpdateLoading,
        isVerifyEmailUpdateError,
        verifyEmailUpdateError,
        handleVerifyEmailUpdate,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error verifying email update:', error);
    throw error;
  }
};

export const useLogoutAllUsers = () => {
  try {
    // api call mutation
    const [
      logoutAllUsers,
      {
        isLoading: isLogoutAllUsersLoading,
        isError: isLogoutAllUsersError,
        error: logoutAllUsersError,
      },
    ] = useLogoutAllUsersMutation();

    // handlers
    const handleLogoutAllUsers = async data => {
      return await logoutAllUsers(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isLogoutAllUsersLoading,
        isLogoutAllUsersError,
        logoutAllUsersError,
        logoutAllUsers: handleLogoutAllUsers,
      }),
      [
        isLogoutAllUsersLoading,
        isLogoutAllUsersError,
        logoutAllUsersError,
        handleLogoutAllUsers,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error logging out all users:', error);
    throw error;
  }
};
